import React, { useCallback } from 'react'

import {
  Badge,
  Button,
  FileFull,
  Icon,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import axios from 'axios'
import { arrayOf, func, number, shape, string } from 'prop-types'

import LegacyDataTable from 'Pods/Shared/DataTable/LegacyDataTable'
import useEasytabsVisibility from 'Pods/Shared/Hooks/useEasytabsVisibility'
import useNotifications from 'Pods/Shared/Hooks/useNotifications'
import I18n from 'Pods/Shared/I18n'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import useSubscriptionSettingsTracking from '../../../hooks/useSubscriptionSettingsTracking'

const STATUS_BADGE_TYPE = {
  paid: 'light-green',
  pending: 'attention',
  failed: 'light-red',
}

function StatusBadge({ value, status }) {
  return (
    <Badge type={STATUS_BADGE_TYPE[status] || 'attention'}>
      {value}
    </Badge>
  )
}

StatusBadge.propTypes = {
  value: string.isRequired,
  status: string.isRequired,
}

function InvoiceButton({ invoiceUrl, paymentId, downloadLabel, onDownloadClick, onDownloadFailed }) {
  const handleClick = async () => {
    onDownloadClick(paymentId)

    // Fetch the invoice manually instead of using a plain href so we can
    // detect and track download failures via onDownloadFailed.
    try {
      const response = await axios.get(invoiceUrl, { responseType: 'blob' })

      const blob = response.data
      const url = URL.createObjectURL(blob)
      const link = Object.assign(document.createElement('a'), {
        href: url,
        download: `invoice-${paymentId}.pdf`,
      })
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch (error) {
      onDownloadFailed(paymentId, error.message)
    }
  }

  return (
    <Button
      type="secondary"
      size="small"
      trailIcon={<Icon svgComponent={FileFull} size="small" color="dark" />}
      onClick={handleClick}
      customClass="payment-history__invoice-button"
    >
      {downloadLabel}
    </Button>
  )
}

InvoiceButton.propTypes = {
  invoiceUrl: string.isRequired,
  paymentId: number.isRequired,
  downloadLabel: string.isRequired,
  onDownloadClick: func.isRequired,
  onDownloadFailed: func.isRequired,
}

function PaymentHistory({ payments = EMPTY_ARRAY, subscriptionId, tier = null, locale = 'en' }) {
  const {
    trackPaymentHistoryViewed,
    trackInvoiceDownloadClick,
    trackInvoiceDownloadFailed,
  } = useSubscriptionSettingsTracking(tier)
  const { showError } = useNotifications()

  const handleTabVisible = useCallback(() => {
    trackPaymentHistoryViewed()
  }, [trackPaymentHistoryViewed])

  useEasytabsVisibility('tab-settings-subscription', handleTabVisible)

  const translate = useCallback(
    (key, options = {}) => I18n.t(key, { ns: 'rails', lng: locale, ...options }),
    [locale]
  )

  const downloadLabel = translate('subscription_management.payment_history.download')

  const columnsConfig = {
    id: { visible: false },
    invoiceUrl: { visible: false },
    status: { visible: false },
    amountPaid: {
      title: translate('subscription_management.payment_history.columns.amount_paid'),
      position: 1,
    },
    currency: {
      title: translate('subscription_management.payment_history.columns.currency'),
      position: 2,
    },
    paymentDate: {
      title: translate('subscription_management.payment_history.columns.payment_date'),
      position: 3,
    },
    statusLabel: {
      title: translate('subscription_management.payment_history.columns.status'),
      position: 4,
    },
    invoice: {
      title: translate('subscription_management.payment_history.columns.invoice'),
      position: 5,
    },
    paymentMethod: {
      title: translate('subscription_management.payment_history.columns.payment_method'),
      position: 6,
    },
    billingPeriod: {
      title: translate('subscription_management.payment_history.columns.billing_period'),
      position: 7,
    },
  }

  const tableData = payments.map((payment) => ({
    ...payment,
    invoice: payment.invoiceUrl,
  }))

  return (
    <div className="payment-history">
      <h3 className="cgt-legacy-heading-h4">
        {translate('subscription_management.payment_history.title')}
      </h3>
      <LegacyDataTable
        data={tableData}
        columnsConfig={columnsConfig}
        renderCustomCell={{
          statusLabel: (value, row) => (
            <StatusBadge value={value} status={row.status} />
          ),
          invoice: (value, row) => (
            <InvoiceButton
              invoiceUrl={row.invoiceUrl}
              paymentId={row.id}
              downloadLabel={downloadLabel}
              onDownloadClick={(paymentId) => trackInvoiceDownloadClick(subscriptionId, paymentId)}
              onDownloadFailed={(paymentId, error) => {
                trackInvoiceDownloadFailed(subscriptionId, paymentId, error)
                showError(translate('subscription_management.payment_history.download_error'))
              }}
            />
          ),
        }}
      />
    </div>
  )
}

PaymentHistory.propTypes = {
  subscriptionId: number.isRequired,
  payments: arrayOf(
    shape({
      id: number.isRequired,
      amountPaid: string.isRequired,
      currency: string.isRequired,
      paymentDate: string.isRequired,
      status: string.isRequired,
      statusLabel: string.isRequired,
      invoiceUrl: string.isRequired,
      paymentMethod: string.isRequired,
      billingPeriod: string.isRequired,
    })
  ),
  tier: string,
  locale: string,
}

export default PaymentHistory
